import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import ScoreBadge from "@/components/ScoreBadge";
import InsightsList from "@/components/InsightsList";
import ReflectionsList from "@/components/ReflectionsList";
import { ArrowRight, Sparkles } from "lucide-react";
import { todayLocalISO } from "@/lib/calendar";

const ZONE_DESTINATIONS = {
  head: "/correlations?focus=head",
  heart: "/correlations?focus=heart",
  torso: "/dashboard",
  legs: "/correlations?focus=legs",
};

const ZONE_DESCRIPTIONS = {
  head: { title: "Cabeza", text: "Patrones de sueño y pensamientos" },
  heart: { title: "Corazón", text: "Patrones emocionales" },
  torso: { title: "Bienestar", text: "Dashboard general" },
  legs: { title: "Cuerpo", text: "Patrones de actividad física" },
};

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [hoverZone, setHoverZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState([]);
  const [insightsMeta, setInsightsMeta] = useState({ learning: false, message: null });
  const [reflections, setReflections] = useState(null); // null = loading, [] = empty

  useEffect(() => {
    let alive = true;
    const todayISO = todayLocalISO();
    api.get("/dashboard/summary", { params: { today: todayISO } }).then(({ data }) => {
      if (alive) setSummary(data);
    }).finally(() => alive && setLoading(false));
    api.get("/dashboard/insights", { params: { days: "30", today: todayISO } }).then(({ data }) => {
      if (!alive) return;
      setInsights((data.insights || []).slice(0, 2));
      setInsightsMeta({ learning: !!data.learning, message: data.message });
    });
    api.get("/logs/reflections", { params: { limit: 3 } }).then(({ data }) => {
      if (alive) setReflections(data.reflections || []);
    }).catch(() => alive && setReflections([]));
    return () => { alive = false; };
  }, []);

  const today = new Date().toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Buen día";
    if (h < 19) return "Buenas tardes";
    return "Buenas noches";
  })();

  return (
    <div className="space-y-8 animate-fade-up" data-testid="home-page">
      <header className="flex flex-col gap-1 pt-2">
        <p className="tk-label">{today}</p>
        <h1 className="font-outfit text-3xl sm:text-4xl text-ink-900 leading-tight">
          {greeting}, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="font-dmsans text-ink-600 text-sm">
          {summary?.has_today_log
            ? summary.today_completed
              ? "Ya completaste tu registro de hoy. Cuidate."
              : "Tu registro de hoy está empezado. ¿Lo completamos?"
            : "Es momento de hacer tu registro diario."}
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Avatar interactive zone */}
        <div className="tk-card flex flex-col items-center" data-testid="home-avatar-card">
          <div
            className="relative w-full max-w-[260px] mx-auto"
            onMouseLeave={() => setHoverZone(null)}
          >
            <Avatar
              gender={user?.gender || "hombre"}
              onZone={(zone) => {
                setHoverZone(zone);
                setTimeout(() => navigate(ZONE_DESTINATIONS[zone] || "/log"), 120);
              }}
            />
          </div>
          <div className="mt-2 text-center min-h-[48px]" data-testid="avatar-hint">
            {hoverZone ? (
              <>
                <p className="tk-label text-sage-600">{ZONE_DESCRIPTIONS[hoverZone].title}</p>
                <p className="font-dmsans text-sm text-ink-900 mt-0.5">{ZONE_DESCRIPTIONS[hoverZone].text}</p>
              </>
            ) : (
              <p className="font-dmsans text-sm text-ink-600">Tocá una zona para explorar tus patrones.</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="tk-card" data-testid="home-cta-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="tk-label">Hoy</p>
                <h3 className="font-outfit text-2xl text-ink-900 mt-1">Registro diario</h3>
                <p className="font-dmsans text-sm text-ink-600 mt-1">Un registro por día. Podés guardar y volver más tarde.</p>
              </div>
              {summary?.overall?.daily != null && (
                <ScoreBadge score={summary.overall.daily} testId="home-today-score" />
              )}
            </div>
            <button
              onClick={() => navigate("/log")}
              className="tk-btn-primary w-full mt-5"
              data-testid="home-open-log"
            >
              {summary?.has_today_log ? "Continuar registro" : "Empezar registro de hoy"}
              <ArrowRight size={16} />
            </button>
          </div>

          <div className="tk-soft-card" data-testid="home-summary-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-sage-600" />
              <p className="tk-label">Tu bienestar</p>
            </div>
            {loading ? (
              <div className="h-16 animate-pulse bg-sage-100/60 rounded-2xl" />
            ) : summary?.logs_count > 0 ? (
              <div className="grid grid-cols-3 gap-3">
                <ScoreColumn label="Hoy" score={summary.overall.daily} />
                <ScoreColumn label="Semana" score={summary.overall.weekly} />
                <ScoreColumn label="Mes" score={summary.overall.monthly} />
              </div>
            ) : (
              <p className="font-dmsans text-sm text-ink-600">
                Aún no tenemos información suficiente. Completá tu primer registro para empezar a descubrir tus patrones.
              </p>
            )}
            <button
              onClick={() => navigate("/dashboard")}
              className="tk-btn-ghost w-full mt-4"
              data-testid="home-open-dashboard"
            >
              Ver dashboard completo <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {reflections !== null && (
        <section className="space-y-3" data-testid="home-reflections-section">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-outfit text-xl text-ink-900">Tus reflexiones recientes</h2>
            <p className="tk-label">Tu voz interior</p>
          </div>
          <ReflectionsList items={reflections} />
        </section>
      )}

      {(insights.length > 0 || insightsMeta.learning) && (
        <section className="space-y-3" data-testid="home-insights-section">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-outfit text-xl text-ink-900">Lo que estamos descubriendo</h2>
            <button
              onClick={() => navigate("/correlations")}
              className="font-dmsans text-xs text-sage-600 hover:text-sage-700 transition"
              data-testid="home-insights-view-all"
            >
              Ver todos →
            </button>
          </div>
          <InsightsList
            items={insights}
            learning={insightsMeta.learning}
            message={insightsMeta.message}
            variant="compact"
          />
        </section>
      )}
    </div>
  );
}

function ScoreColumn({ label, score }) {
  return (
    <div className="text-center">
      <p className="tk-label">{label}</p>
      <p className="font-outfit text-3xl text-ink-900 mt-1">
        {score == null ? "—" : Number(score).toFixed(1)}
      </p>
      <p className="font-dmsans text-[10px] text-ink-600">/10</p>
    </div>
  );
}
